import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { reactive, ref, computed, watch, PropType, nextTick } from 'vue'
import { useCmTableListRefresh } from '@/hooks/use-cm-table-list-refresh'
import { useI18n } from 'vue-i18n'
import CmTable from '@/components/cmTable/index.vue'
import ExportDropdown from '@/views/dashboard/_components/export-dropdown/export-dropdown.vue'

import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import GroupTreeSelector from '@/views/dashboard/_components/group-tree-selector/group-tree-selector.vue'
import BtnIcon from '@/views/dashboard/_components/btn-icon/btn-icon.vue'
import {
  RemoteAuthLoginService,
  RequestParams,
  AuthLogItem,
} from '@/views/dashboard/remote-authentication/service'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import {
  ActionType,
  FormSize,
  FormType,
  ObjectFormItem,
  DateType,
} from '@/components/cmForm/types'
import { TableOpt, TableActOpt } from '@/components/cmTable/types'
import { TableViewMode, TableViewModeType } from '@/views/dashboard/_enum/table-view-mode'
import { computerInfoCols } from '@/views/dashboard/data-safe/ransomware-protection/types/ransomware-incident'
import dayjs from 'dayjs'
import { useParseTime } from '@/hooks'
import { formatTimeStamp } from '@/utils/time'
import { formatMapToOptions } from '@/utils/tool'
import RawDataDrawer from '@/views/dashboard/_components/raw-data-drawer/raw-data-drawer.vue'
import { getLogTypeLabel } from '@/views/dashboard/remote-authentication/types'
import { ServicePermissionService, PermissionFe } from '@/services/service-permission.service'
import { listCol } from '@/utils/list-columns-i18n'

type ExcludeRequestParams = Exclude<keyof RequestParams, 'limit' | 'page' | 'device_id'>

export default /*#__PURE__*/_defineComponent({
  __name: 'remote-login-log-list',
  props: {
  viewMode: {
    type: String as PropType<TableViewModeType>,
    default: TableViewMode.preview
  },
  /** 全量侧栏：与排行点击一致时不默认时间 */
  initialDateEmpty: {
    type: Boolean,
    default: false,
  },
  /** 全量侧栏：与排行图 TimeRange 一致的起止时间（Unix 秒） */
  initialTimeRangeUnix: {
    type: Array as unknown as PropType<[number, number] | undefined>,
    default: undefined,
  },
  /** 全量侧栏：预填搜索关键字（如计算机名） */
  initialKeyword: {
    type: String,
    default: undefined,
  },
  /** 全量侧栏：预填分组 id */
  initialGroupId: {
    type: String,
    default: undefined,
  },
},
  emits: ['viewAll', 'rawData'],
  setup(__props, { expose: __expose, emit: __emit }) {

const { t, locale } = useI18n()
const props = __props

const emits = __emit

const injector = useReflectiveInjector()
const remoteAuthLoginService = injector.get(RemoteAuthLoginService)
const servicePermissionService = injector.get(ServicePermissionService)
const dataApi = remoteAuthLoginService.getTerminalUserLoginLogPage.bind(remoteAuthLoginService)
const hasPermission = servicePermissionService.getHasPermission(PermissionFe.remote_control)

const tableRef = ref()
const { refreshList } = useCmTableListRefresh(tableRef)
const total = computed(() => tableRef.value?.total || 0)
const logTypeLabel = computed(() => {
  void locale.value
  return getLogTypeLabel(t)
})

function resolveFullInitialTime():
  | { date: [string, string] | undefined; start: number | undefined; end: number | undefined }
  | null {
  if (props.viewMode !== TableViewMode.full) return null
  if (props.initialDateEmpty) {
    return { date: undefined, start: undefined, end: undefined }
  }
  const unix = props.initialTimeRangeUnix
  if (unix && unix.length === 2 && unix[0] != null && unix[1] != null) {
    const [a, b] = unix
    return {
      date: [dayjs.unix(a).format('YYYY-MM-DD'), dayjs.unix(b).format('YYYY-MM-DD')] as [string, string],
      start: a,
      end: b,
    }
  }
  const range = [dayjs().subtract(3, 'month').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')] as [string, string]
  const [s, e] = useParseTime(range)
  return { date: range, start: s, end: e }
}

const fullInitial = resolveFullInitialTime()
const initialDateRange = fullInitial != null ? fullInitial.date : undefined
const [initialStartTime, initialEndTime] =
  fullInitial != null
    ? [fullInitial.start, fullInitial.end]
    : useParseTime(initialDateRange || [])

const baselineKeyword =
  props.initialKeyword !== undefined && props.initialKeyword !== null ? props.initialKeyword : ''
const baselineGroupId =
  props.initialGroupId !== undefined && props.initialGroupId !== null ? props.initialGroupId : undefined

const queryParams = reactive<Partial<RequestParams>>({
  date: initialDateRange as (string | number)[] | undefined,
  start_time: initialStartTime,
  end_time: initialEndTime,
  event_type: 2, // 远程登录
  log_type: null,
  source_ip: null,
  key_word: baselineKeyword,
  group_id: baselineGroupId,
  page: 1,
  limit: props.viewMode === TableViewMode.preview ? 10 : 20,
})

const filterFormColumns = computed<ObjectFormItem<RequestParams, ExcludeRequestParams>>(() => {
  void locale.value
  return {
    group_id: {
      colOpt:{ style:'flex:0 0 150px' },
      slot: true,
      hiddenAllLabel: true,
      outSearchFiledFlag: true,
      type: FormType.Select,
      events: {
        change: () => void refreshList(),
      },
    },
    date: {
      colOpt: { style: 'flex:0 0 270px' },
      hiddenAllLabel: true,
      outSearchFiledFlag: true,
      type: FormType.Date,
      dateType: DateType.DateRange,
      events: {
        change: (val) => {
          if (val) {
            const [start, end] = useParseTime(val)
            queryParams.start_time = start
            queryParams.end_time = end
          } else {
            queryParams.start_time = undefined
            queryParams.end_time = undefined
          }
          void refreshList()
        },
      },
      dateOpt: {
        'value-format': 'YYYY-MM-DD',
        'start-placeholder': t('portManage.startTime'),
        'end-placeholder': t('portManage.endTime'),
        clearable: true,
      },
    },
    log_type: {
      colOpt: { style: 'width: 110px;flex:0 0 auto' },
      label: t('remoteLoginLog.logType'),
      hiddenAllLabel: true,
      outSearchFiledFlag: true,
      type: FormType.Select,
      selectOpt: {
        clearable: true,
      },
      events: {
        change: () => { void refreshList() },
      },
      dictData: formatMapToOptions(logTypeLabel.value, true)
    },
  }
})

const isShowRawData = ref(false)
const currentRow = ref<any>({})

const tableActOpt = computed<TableActOpt>(() => {
  void locale.value
  return {
    columnOpt: { label: listCol(t, 'operation'), width: 120, align: 'left', fixed: 'right' },
    actionButtons: (row: AuthLogItem) => [
      {
        name: listCol(t, 'dataSource'),
        type: 'rawData',
        opt: { size: FormSize.Small, text: true, type: 'text' },
      },
    ],
  }
})

const tableOpt = computed<TableOpt>(() => {
  void locale.value
  return {
    bodyStyle: props.viewMode === TableViewMode.full ? 'padding:10px 16px' : 'background: transparent;padding:0',
    containerOpt: { style: 'padding: 0;background: var(--cm-color-card-bg)' },
    path: 'data',
    height: props.viewMode === TableViewMode.preview ? undefined : '100%',
    showActionBar: false,
    showForm: false,
    showRightToolbar: false,
    rowKey: 'id',
    selection: false,
    showTip: false,
    usePagination: false,
    disableScrollLoad: props.viewMode === TableViewMode.preview,
    scrollLoadThreshold: 10,
    scrollLoadDelay: 100,
    columns: [
      {
        label: listCol(t, 'loginTime'),
        prop: 'log_time',
        width: 150,
        formatter: (_row: any, value: number) => formatTimeStamp(value, 'YYYY-MM-DD HH:mm'),
      },
      ...computerInfoCols(true, false, t),
      {
        label: listCol(t, 'loginSourceIp'),
        prop: 'source_ip',showOverflow:true
      },
      {
        label: listCol(t, 'loginProtocol'),
        prop: 'login_protocol',showOverflow:true
      },
      {
        label: listCol(t, 'loginAccount'),
        prop: 'name',showOverflow:true
      },
      {
        label: listCol(t, 'loginRole'),
        prop: 'is_admin',showOverflow:true
      },
      {
        label: listCol(t, 'result'),
        prop: 'log_type',showOverflow:true
      },
    ],
  }
})

/** 全量侧栏：关键字 / 分组 / 时间与嵌入 props 一致（重置与排行打开后同步顶部搜索框） */
function applyFullModeEmbedBaselines() {
  if (props.viewMode !== TableViewMode.full) return
  const kw =
    props.initialKeyword !== undefined && props.initialKeyword !== null ? props.initialKeyword : ''
  queryParams.key_word = kw
  queryParams.group_id =
    props.initialGroupId !== undefined && props.initialGroupId !== null ? props.initialGroupId : undefined
  if (props.initialDateEmpty) {
    queryParams.date = undefined
    queryParams.start_time = undefined
    queryParams.end_time = undefined
  } else {
    const unix = props.initialTimeRangeUnix
    if (unix && unix.length === 2 && unix[0] != null && unix[1] != null) {
      const [a, b] = unix
      queryParams.date = [dayjs.unix(a).format('YYYY-MM-DD'), dayjs.unix(b).format('YYYY-MM-DD')] as (string | number)[]
      queryParams.start_time = a
      queryParams.end_time = b
    } else {
      const range = [dayjs().subtract(3, 'month').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')] as [string, string]
      const [s, e] = useParseTime(range)
      queryParams.date = range
      queryParams.start_time = s
      queryParams.end_time = e
    }
  }
}

watch(
  [
    () => props.viewMode,
    () => (props.viewMode === TableViewMode.full ? props.initialKeyword : undefined),
    () => (props.viewMode === TableViewMode.full ? props.initialGroupId : undefined),
    () => (props.viewMode === TableViewMode.full ? props.initialDateEmpty : undefined),
    () => (props.viewMode === TableViewMode.full ? props.initialTimeRangeUnix?.[0] : undefined),
    () => (props.viewMode === TableViewMode.full ? props.initialTimeRangeUnix?.[1] : undefined),
  ],
  () => {
    if (props.viewMode !== TableViewMode.full) return
    applyFullModeEmbedBaselines()
  },
  { flush: 'post', immediate: true }
)

async function handleSearch(value: string) {
  await nextTick()
  queryParams.key_word = value
  void refreshList()
}

async function handleQuery(form: any) {
  Object.assign(queryParams, form)
  await refreshList()
}

function handleReset() {
  queryParams.log_type = null
  queryParams.source_ip = null
  if (props.viewMode === TableViewMode.full) {
    applyFullModeEmbedBaselines()
  } else {
    queryParams.key_word = ''
    queryParams.group_id = undefined
    queryParams.date = undefined
    queryParams.start_time = undefined
    queryParams.end_time = undefined
  }
  void refreshList()
}

async function updateGroupId(groupId: number) {
  // GroupTreeSelector 初始化会回传当前分组；未变化时不重复请求
  if (String(queryParams.group_id ?? '') === String(groupId)) return
  queryParams.group_id = String(groupId)
  queryParams.page = 1
  await refreshList()
}

function handleAction({ type, rowItem }: { type: any; rowItem?: AuthLogItem }) {
  if (type === 'rawData' && rowItem) {
    currentRow.value = rowItem
    isShowRawData.value = true
  }
}

function download(type: 0 | 1) {
  const params: Partial<RequestParams> = {
    event_type: queryParams.event_type,
    log_type: queryParams.log_type,
    source_ip: queryParams.source_ip,
    key_word: queryParams.key_word,
    group_id: queryParams.group_id,
    start_time: queryParams.start_time,
    end_time: queryParams.end_time,
  }
  remoteAuthLoginService.getTerminalUserLoginLogExport(type, params, t('portManage.exportRemoteLogin'))
}

/** 全量侧栏基准由 watch(immediate) 写入 queryParams；首屏仅 CmTable 默认拉一次，避免与原 onMounted(refreshList) 双发 */
__expose({
  tableRef,
  getList: () => void refreshList(),
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(CmTable, {
      ref_key: "tableRef",
      ref: tableRef,
      tableOpt: tableOpt.value,
      tableActOpt: tableActOpt.value,
      "has-permission": _unref(hasPermission),
      api: _unref(dataApi),
      modelValue: queryParams,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((queryParams) = $event)),
      pageOption: {
      style: 'padding-bottom: 16px',
      pageNumName: 'page',
      pageSizeName: 'limit',
    },
      onHandleAction: handleAction
    }, {
      header: _withCtx(() => [
        (props.viewMode !== _unref(TableViewMode).preview)
          ? (_openBlock(), _createBlock(SearchFilterBar, {
              key: 0,
              modelValue: queryParams,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((queryParams) = $event)),
              "form-columns": filterFormColumns.value,
              disabled: !_unref(hasPermission),
              "show-search": true,
              "sync-search-from-field": "key_word",
              "search-placeholder": _ctx.$t('remoteLoginLog.searchPlaceholder'),
              "popover-threshold": 2,
              onSearch: handleSearch,
              onQuery: handleQuery,
              onReset: handleReset
            }, {
              group_id: _withCtx(() => [
                _createVNode(GroupTreeSelector, {
                  width: "100%",
                  disabled: !_unref(hasPermission),
                  "terminal-type": [1, 3],
                  onSelected: updateGroupId
                }, null, 8, ["disabled"])
              ]),
              actions: _withCtx(() => [
                _createVNode(ExportDropdown, {
                  "has-permission": _unref(hasPermission),
                  onExport: download
                }, null, 8, ["has-permission"])
              ]),
              _: 1
            }, 8, ["modelValue", "form-columns", "disabled", "search-placeholder"]))
          : _createCommentVNode("", true)
      ]),
      name: _withCtx(({ row }: any) => [
        _createElementVNode("span", null, _toDisplayString(row.domain + '\\' + row.name), 1)
      ]),
      is_admin: _withCtx(({ value }: any) => [
        _createElementVNode("span", null, _toDisplayString(value === 1 ? _ctx.$t('remoteLoginLog.admin') : _ctx.$t('remoteLoginLog.nonAdmin')), 1)
      ]),
      log_type: _withCtx(({ value }: any) => [
        _createElementVNode("span", null, _toDisplayString(logTypeLabel.value[value]), 1)
      ]),
      _: 1
    }, 8, ["tableOpt", "tableActOpt", "has-permission", "api", "modelValue"]),
    _createVNode(RawDataDrawer, {
      modelValue: isShowRawData.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((isShowRawData).value = $event)),
      "raw-data": currentRow.value
    }, null, 8, ["modelValue", "raw-data"])
  ], 64))
}
}

})